// spec/frontend/screens/app-shell/app-shell.md: frontend-screens-app-shell-R06/R07/R08.
import { createRouter, createWebHistory } from 'vue-router';
import type { Router } from 'vue-router';
import { ElMessage } from 'element-plus';
import AppLayout from '@/app/layouts/AppLayout.vue';
import { useAuthStore } from '@/entities/auth';
import { i18n } from '@/shared/i18n';
import { isSection } from '@/shared/config/privileges';
import { getConfig } from '@/shared/config/runtime';

/**
 * Создаёт router с правильным base path из конфигурации
 * ВАЖНО: Вызывать ПОСЛЕ loadConfig()!
 */
export function createAppRouter(): Router {
  // Получаем base path из загруженного конфига
  const config = getConfig();
  let basePath = '/';
  
  if (config.basePath !== undefined && config.basePath.length > 0) {
    // Убедимся что путь начинается с / и заканчивается на /
    basePath = config.basePath;
    if (!basePath.startsWith('/')) basePath = `/${basePath}`;
    if (!basePath.endsWith('/')) basePath = `${basePath}/`;
  }

  const router = createRouter({
    history: createWebHistory(basePath),
    routes: [
    // Protected routes
    //
    // Проектные разделы помечены `projectScoped`: их содержимое зависит от
    // проекта, поэтому проект входит в адрес и восстанавливается по ссылке
    // (app-shell-shell-routing-R12). Разделы, работающие ещё и в контексте
    // фичи, помечены `featureScoped`: фича в адресе необязательна, но
    // выбранная в нём отражается. Разбор и поддержание адреса — providers/
    // project-context.ts.
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'source-explorer',
          component: (): Promise<
            typeof import('@/pages/source-explorer/SourceExplorerPage.vue')
          > => import('@/pages/source-explorer/SourceExplorerPage.vue'),
          meta: { projectScoped: true },
        },
        {
          path: '/business-model',
          name: 'business-model',
          component: (): Promise<typeof import('@/pages/business-model/BusinessModelPage.vue')> =>
            import('@/pages/business-model/BusinessModelPage.vue'),
          meta: { projectScoped: true, featureScoped: true },
        },
        // app-shell-shell-routing-R04/R12; frontend-class-mappings-R30.
        {
          path: '/mappings',
          name: 'mappings',
          component: (): Promise<typeof import('@/pages/mappings/MappingsPage.vue')> =>
            import('@/pages/mappings/MappingsPage.vue'),
          meta: { projectScoped: true, featureScoped: true, mappingScoped: true },
        },
        {
          // Раздел ETL не построен: прямой заход на его путь ведёт в домашний
          // раздел (shell-routing-R08). Прежде путь показывал дерево
          // источников под чужим адресом, и ни один пункт навигации не был
          // подсвечен — адрес расходился с содержимым.
          path: '/etl',
          redirect: '/',
        },
      ],
    },
  ],
  });

  // frontend-screens-app-shell-R07/R08; app-shell-shell-routing-R05/R06/R07.
  // The layout gates the child view too: a direct denied URL never mounts it.
  router.beforeEach(async to => {
    if (to.meta['requiresAuth'] !== true) return true;
    const auth = useAuthStore();
    if (!auth.isAuthenticated) {
      try {
        if (!await auth.initializeAuth()) {
          auth.startLogin(router.resolve(to.fullPath).href);
          return false;
        }
      } catch {
        // The profile error is shown by the store; it does not turn into logout.
        return false;
      }
    }
    // frontend-screens-app-shell-R10/R11: '/' also hosts the common shell
    // for permitted creation when no subject section is available.
    const creationEntry = to.name === 'source-explorer' &&
      (auth.hasPrivilege('metabridge.projects.edit') || auth.hasPrivilege('metabridge.versions.edit'));
    if (isSection(to.name) && !auth.canAccessSection(to.name) && !creationEntry) {
      ElMessage.error(i18n.global.t('errors.HTTP_403'));
    }
    return true;
  });

  return router;
}

